/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {inject, Injectable, InjectionToken} from '@angular/core';

/**
 * Controls which footer links are visible for a given origin.
 * All flags default to `true`; a brand entry overrides only the links it wants to hide.
 */
export interface FooterLinksConfig {
  siemensDigitalProductPassport: boolean;
  aboutBatteryPassEU: boolean;
  teamcenterPCM: boolean;
  catenaX: boolean;
}

export interface LogoEntry {
  hostname: string;
  light: string;
  dark: string;
  /** Overrides the navbar logo width in pixels. */
  width?: number;
  /** Overrides the navbar logo height in pixels. */
  height?: number;
  /** When `true`, the entire public-page footer is hidden regardless of `footerLinks`. */
  hideFooter?: boolean;
  footerLinks?: Partial<FooterLinksConfig>;
}

interface LogoPair {
  light: string;
  dark: string;
}

export interface LogoDimensions {
  width?: number;
  height?: number;
}

const ALL_LINKS_VISIBLE: FooterLinksConfig = {
  siemensDigitalProductPassport: true,
  aboutBatteryPassEU: true,
  teamcenterPCM: true,
  catenaX: true,
};

const SIEMENS_LOGO: LogoPair = {
  light: 'assets/img/sie-logo-black-rgb.svg',
  dark: 'assets/img/sie-logo-white-rgb.svg',
};

const DEFAULT_LOGO_MAP: LogoEntry[] = [
  {
    hostname: 'cummins.com',
    light: 'assets/img/cummins-logo.svg',
    dark: 'assets/img/cummins-logo.svg',
    width: 50,
    height: 30,
    hideFooter: true,
  },
];

export const LOGO_MAP = new InjectionToken<LogoEntry[]>('LOGO_MAP', {
  providedIn: 'root',
  factory: () => DEFAULT_LOGO_MAP,
});

@Injectable({providedIn: 'root'})
export class LogoService {
  private readonly logoMap = inject(LOGO_MAP);

  /**
   * Returns the logo pair (light/dark SVG paths) for the given origin.
   * Falls back to the Siemens logo when no brand entry matches.
   *
   * @param origin - The page origin (defaults to `window.location.origin`).
   * @returns The matching {@link LogoPair}, or the Siemens default.
   */
  resolve(origin = window.location.origin): LogoPair {
    return this.findEntry(origin) ?? SIEMENS_LOGO;
  }

  /**
   * Returns the footer link visibility config for the given origin.
   * Unmatched origins receive the all-visible default.
   *
   * @param origin - The page origin (defaults to `window.location.origin`).
   * @returns A {@link FooterLinksConfig} with each link's visibility flag.
   */
  resolveFooterLinks(origin = window.location.origin): FooterLinksConfig {
    return {...ALL_LINKS_VISIBLE, ...this.findEntry(origin)?.footerLinks};
  }

  /**
   * Returns `true` when the entire footer should be hidden for the given origin.
   * Takes precedence over individual link flags.
   *
   * @param origin - The page origin (defaults to `window.location.origin`).
   */
  resolveHideFooter(origin = window.location.origin): boolean {
    return this.findEntry(origin)?.hideFooter ?? false;
  }

  /**
   * Returns the custom logo dimensions for the given origin, if configured.
   * Returns an empty object when no override is defined.
   *
   * @param origin - The page origin (defaults to `window.location.origin`).
   * @returns The custom logo dimensions, or an empty object if none are configured.
   */
  resolveLogoDimensions(origin = window.location.origin): LogoDimensions {
    const entry = this.findEntry(origin);
    const result: LogoDimensions = {};
    if (entry?.width !== undefined) {
      result.width = entry.width;
    }
    if (entry?.height !== undefined) {
      result.height = entry.height;
    }
    return result;
  }

  private findEntry(origin: string): LogoEntry | undefined {
    let hostname = '';
    try {
      hostname = new URL(origin).hostname;
    } catch {
      return undefined;
    }
    return this.logoMap.find(
      (e) => hostname === e.hostname || hostname.endsWith(`.${e.hostname}`),
    );
  }
}
